import React, { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

export default function Placeholder(props) {
  const materialRef = useRef();
  const count = 12; // Reduced count slightly for optimization

  const { offsets, speeds, lengths, angles } = useMemo(() => {
    const offsets = new Float32Array(count * 3);
    const speeds = new Float32Array(count);
    const lengths = new Float32Array(count);
    const angles = new Float32Array(count);

    for (let i = 0; i < count; i++) {
        // Tighter bundle
        const r = Math.random() * 1; 
        const theta = Math.random() * Math.PI * 2;
        
        offsets[i * 3] = r * Math.cos(theta);
        offsets[i * 3 + 1] = (Math.random() - 0.5) * 5.0;
        offsets[i * 3 + 2] = r * Math.sin(theta);
        
        speeds[i] = 1.0 + Math.random() * 1.5;
        lengths[i] = 0.5 + Math.random() * 1.5;
        angles[i] = Math.random() * Math.PI; // Random rotation
    }
    return { offsets, speeds, lengths, angles };
  }, []);

  const shaderArgs = useMemo(() => ({
    uniforms: {
      uTime: { value: 0 },
      uColor: { value: new THREE.Color("#79a2d5") },
    },
    vertexShader: `
      uniform float uTime;
      
      attribute vec3 aOffset;
      attribute float aSpeed;
      attribute float aLength;
      attribute float aAngle;
      
      varying float vAlpha;
      varying vec2 vUv;

      void main() {
        vUv = uv;
        vec3 tPos = position;
        
        // Scale height
        tPos.y *= aLength;
        
        // Rotate the plane around Y axis to face random directions
        // effectively prevents them from disappearing at certain angles
        float c = cos(aAngle);
        float s = sin(aAngle);
        // Plane is initially facing +Z (normal), so we rotate x and z
        float x = tPos.x * c - tPos.z * s;
        float z = tPos.x * s + tPos.z * c;
        tPos.x = x;
        tPos.z = z;
        
        // Animate up
        float bounds = 2.0;
        float yMove = uTime * aSpeed * 2.0;
        float y = mod(aOffset.y + yMove, bounds);
        
        vec3 instancePos = vec3(aOffset.x, y, aOffset.z);
        
        vec4 mvPosition = modelViewMatrix * vec4(instancePos + tPos, 1.0);
        gl_Position = projectionMatrix * mvPosition;
        
        vAlpha = 0.8;
      }
    `,
    fragmentShader: `
      uniform vec3 uColor;
      varying float vAlpha;
      varying vec2 vUv;
      
      void main() {
        // Fade both ends to create a soft streak without solid edges
        // sin(0 to PI) creates a 0->1->0 opacity curve
        float fade = sin(vUv.y * 3.14159);
        gl_FragColor = vec4(uColor * 2.0, fade * vAlpha);
      }
    `,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide, // Ensure visible from both sides
  }), []);

  useFrame((state, delta) => {
    if (materialRef.current) {
      materialRef.current.uniforms.uTime.value += delta;
    }
  });

  return (
    <instancedMesh args={[null, null, count]} {...props}>
      <planeGeometry args={[0.04, 1]}>
         <instancedBufferAttribute attach="attributes-aOffset" args={[offsets, 3]} />
         <instancedBufferAttribute attach="attributes-aSpeed" args={[speeds, 1]} />
         <instancedBufferAttribute attach="attributes-aLength" args={[lengths, 1]} />
         <instancedBufferAttribute attach="attributes-aAngle" args={[angles, 1]} />
      </planeGeometry>
      <shaderMaterial ref={materialRef} args={[shaderArgs]} />
    </instancedMesh>
  );
}
